import React from 'react';
import { ScrollReveal } from './animations/ScrollReveal';
import { ImageFadeIn } from './animations/ImageFadeIn';
import { useDonateByMail } from '../contexts/DonateByMailContext';
interface Hero {
  title: string;
  subtitle: string;
  backgroundImage: string;
}
interface Stat {
  value: string;
  label: string;
  icon: ReactNode;
}
interface Card {
  title: string;
  content: string;
  image: string;
}
interface SubpageLayoutProps {
  hero: Hero;
  videoSection?: ReactNode;
  stats: Stat[];
  cards: Card[];
  ctaTitle: string;
  ctaDescription: string;
  ctaButtonText: string;
  ctaButtonLink: string;
  children?: ReactNode;
}
const SubpageLayout: React.FC<SubpageLayoutProps> = ({
  hero,
  videoSection,
  stats,
  cards,
  ctaTitle,
  ctaDescription,
  ctaButtonText,
  ctaButtonLink,
  children
}) => {
  const {
    openDonateByMail
  } = useDonateByMail();
  return <div className="w-full min-h-screen bg-white" data-id="element-238" data-mp="1fptz0f1jhaz">
      {/* Hero Section */}
      <div className="relative min-h-[60vh] flex items-center" data-id="element-239" data-mp="5eozam4opg2y">
        <div className="absolute inset-0 z-0" data-id="element-240" data-mp="1ut8zg39dp88t">
          <ImageFadeIn src={hero.backgroundImage} alt={hero.title} className="w-full h-full object-cover" data-id="element-241" data-mp="1uw4xdrl87wx9" />
          <div className="absolute inset-0 bg-black/50" data-id="element-242" data-mp="24shpwm51c4qf"></div>
        </div>
        <div className="relative z-10 w-full pt-36 sm:pt-52 md:pt-56 pb-16 sm:pb-20" data-id="element-243" data-mp="1zteiiv1l1mfa">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center" data-id="element-244" data-mp="1c3k62el6dzvk">
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold text-white tracking-tight mb-4 sm:mb-6" data-id="element-245" data-mp="1c3twg3a4u91s">
              {hero.title}
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-white/90 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-246" data-mp="1ltlvb9p9loc2">
              {hero.subtitle}
            </p>
          </div>
        </div>
      </div>
      {/* Video Section (if provided) */}
      {videoSection}
      {/* Stats Section */}
      <ScrollReveal animation="fade" data-id="element-247" data-mp="oehthl5eex7x">
        <div className="pt-8 sm:pt-12 pb-16 sm:pb-24 bg-gray-50" data-id="element-248" data-mp="odgej5l9hqkx">
          <div className="max-w-5xl mx-auto px-4 sm:px-6" data-id="element-249" data-mp="1e2wz068l0kre">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-250" data-mp="1e0aqa39yfz7l">
              {stats.map((stat, index) => <ScrollReveal key={index} animation="slide-up" delay={index * 0.1} data-id="element-251" data-mp="1mup4s3mnv5pn" data-mpi={index}>
                  <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 h-full" data-id="element-252" data-mp="1rt0gkjwinqai">
                    <div className="flex items-center mb-4" data-id="element-253" data-mp="v4u6vikqm7pd">
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mr-4" data-id="element-254" data-mp="z42x4dgkjng6">
                        {stat.icon}
                      </div>
                      <div className="text-3xl font-bold text-gray-900" data-id="element-255" data-mp="192r2abngg82g">
                        {stat.value}
                      </div>
                    </div>
                    <p className="text-gray-600" data-id="element-256" data-mp="2fiurux16t6sn">{stat.label}</p>
                  </div>
                </ScrollReveal>)}
            </div>
          </div>
        </div>
      </ScrollReveal>
      {/* Cards Section */}
      <ScrollReveal animation="fade" data-id="element-257" data-mp="3vr6vel95zxo"></ScrollReveal>
      {/* Custom Content */}
      {children}
      {/* CTA Section */}
      <ScrollReveal animation="fade" data-id="element-258" data-mp="3v9ww5e2l2j9s">
        <div className="py-16 sm:py-24 bg-israel-blue text-white" data-id="element-259" data-mp="3v8u9jican2zo">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center" data-id="element-260" data-mp="3qc3tgv0ho7e9">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 tracking-tight" data-id="element-261" data-mp="3m9qls126lzeu">
              {ctaTitle}
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-3xl mx-auto" data-id="element-262" data-mp="3hae2treflchj">
              {ctaDescription}
            </p>
            <div className="flex flex-wrap justify-center gap-6" data-id="element-263" data-mp="3cccaefv480te">
              <a href="https://donate.stripe.com/7sY5kw7P1eO24x90y314401" target="_blank" rel="noopener noreferrer" className="inline-block px-8 py-4 bg-white text-israel-blue font-semibold rounded-lg hover:bg-gray-100 transition-colors text-lg shadow-lg" data-id="element-264" data-mp="37czmdrsicnp8">
                Donate Online
              </a>
              <button type="button" onClick={openDonateByMail} className="inline-block px-8 py-4 bg-transparent text-white font-semibold rounded-lg border-2 border-white hover:bg-white/10 transition-colors text-lg" data-id="element-265" data-mp="1ikvn7bljihgw">
                Donate by Mail
              </button>
              <a href={ctaButtonLink} className="inline-block px-8 py-4 bg-transparent text-white font-semibold rounded-lg border-2 border-white hover:bg-white/10 transition-colors text-lg" data-id="element-266" data-mp="uu9ucrk3x3rh">
                {ctaButtonText}
              </a>
            </div>
          </div>
        </div>
      </ScrollReveal>
    </div>;
};
export default SubpageLayout;